import React, { useState, useEffect, useRef } from "react";
import "./WorkTab.css";
import { useFinancialPlanState } from "../FinancialPlanContext";

const WorkTab = ({
  projectId,
  onAddSpecialist,
  AddUserIcon,
  isEditable = true,
}) => {
  const {
    financialPlanData,
    updateFinancialPlanSection,
    loadSectionDetails,
    performCalculation,
    isLoading,
    selectedSpecialists,
    removeSpecialist,
    updateSpecialistEfforts,
    getSpecialistEfforts,
  } = useFinancialPlanState();

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedBlocks, setExpandedBlocks] = useState(new Set()); // ключи "sectionIdx-blockIndex"
  const [localSectionDetails, setLocalSectionDetails] = useState({});
  const [editingFields, setEditingFields] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  const toggleBlockExpanded = (sectionIdx, blockIndex) => {
    const key = `${sectionIdx}-${blockIndex}`;
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const workData = financialPlanData.sections.working_design;
  const [bonusPercent, setBonusPercent] = useState(workData.bonus_percent);
  const [editingBonusPercent, setEditingBonusPercent] = useState(false);
  const [tempBonusPercent, setTempBonusPercent] = useState(bonusPercent);
  const bonusInputRef = React.useRef(null);

  const handleExpandRow = async (idx) => {
    const section = workData.sections[idx];
    const sectionId = section.id;

    // Если секция еще не развернута, загружаем детальные данные
    if (!expandedRows.has(idx) && !localSectionDetails[sectionId]) {
      try {
        const details = await loadSectionDetails(projectId, sectionId);
        setLocalSectionDetails((prev) => ({
          ...prev,
          [sectionId]: details,
        }));
      } catch (error) {
        console.error("Ошибка загрузки детальных данных секции:", error);
      }
    }

    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const handleEditBonusPercent = () => {
    setEditingBonusPercent(true);
    setTempBonusPercent(bonusPercent);
  };

  // Автофокус и выделение текста при входе в режим редактирования процента бонусов
  useEffect(() => {
    if (editingBonusPercent && bonusInputRef.current) {
      bonusInputRef.current.focus();
      const input = bonusInputRef.current;
      requestAnimationFrame(() => {
        try {
          input.select();
        } catch {}
      });
    }
  }, [editingBonusPercent]);

  const handleSaveBonusPercent = async (value) => {
    const numValue = Number(value);
    setBonusPercent(numValue);
    setEditingBonusPercent(false);
    updateFinancialPlanSection("working_design", { bonus_percent: numValue });

    // Запускаем калькуляцию при изменении процента бонусов
    try {
      setIsCalculating(true);
      const calculationResult = await performCalculation(projectId, {
        bonus_percent_construction_design: numValue,
      });

      // Обновляем локальные данные результатами калькуляции
      if (calculationResult && calculationResult.construction_design_detailed) {
        calculationResult.construction_design_detailed.forEach(
          (detailedSection) => {
            if (detailedSection.block_expenditures) {
              setLocalSectionDetails((prev) => ({
                ...prev,
                [detailedSection.section_id]: {
                  ...prev[detailedSection.section_id],
                  block_expenditures:
                    detailedSection.block_expenditures?.map((block) => ({
                      block: {
                        id: block.block?.id || block.block?.tei_id,
                        tei_id: block.block?.tei_id,
                        name:
                          block.block?.name || `Block ${block.block?.tei_id}`,
                        // Поддерживаем новую структуру с performers и старую с performer
                        performers:
                          block.block?.performers ||
                          (block.block?.performer
                            ? [block.block.performer]
                            : []),
                        performer: block.block?.performer, // Оставляем для обратной совместимости
                      },
                      floor_count: block.floor_count || 0,
                      day_efforts: block.day_efforts || 0,
                      bonus_day_efforts: block.bonus_day_efforts || 0,
                      money_expenditures: block.money_expenditures || 0,
                      bonus_money_expenditures:
                        block.bonus_money_expenditures || 0,
                    })) || [],
                  bonus_percent: detailedSection.bonus_percent,
                },
              }));
            }
          }
        );
      }

      // Контекст уже обновлен в performCalculation, дополнительные обновления не нужны
    } catch (error) {
      console.error(
        "Ошибка калькуляции при изменении процента бонусов:",
        error
      );
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCancelBonusPercent = () => {
    setEditingBonusPercent(false);
    setTempBonusPercent(bonusPercent);
  };

  // Функции для редактирования полей в развернутой секции
  const handleStartEdit = (sectionId, blockIndex, fieldName, currentValue) => {
    const fieldKey = `${sectionId}-${blockIndex}-${fieldName}`;
    setEditingFields((prev) => ({
      ...prev,
      [fieldKey]: true,
    }));
    setTempValues((prev) => ({
      ...prev,
      [fieldKey]: currentValue,
    }));
  };

  const handleCancelEdit = (sectionId, blockIndex, fieldName) => {
    const fieldKey = `${sectionId}-${blockIndex}-${fieldName}`;
    setEditingFields((prev) => ({
      ...prev,
      [fieldKey]: false,
    }));
  };

  const handleTempValueChange = (fieldKey, value) => {
    setTempValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  // Функции для работы с исполнителями
  const handleSpecialistDelete = (blockName, sectionName, specialistIndex) => {
    removeSpecialist(sectionName, blockName, specialistIndex);
  };

  const getSpecialistKey = (sectionName, blockName) => {
    return `${sectionName}-${blockName}`;
  };

  // Данные о зарплате загружаются при добавлении исполнителя в SelectSpecialistModal

  // Компонент для редактируемого поля
  const EditableField = ({
    sectionId,
    blockIndex,
    fieldName,
    value,
    isEditing,
    tempValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onTempValueChange,
    format = (v) => v,
    type = "number",
    isEditable = true,
  }) => {
    const fieldKey = `${sectionId}-${blockIndex}-${fieldName}`;
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(
      value === undefined || value === null ? "" : String(value)
    );

    // Обновляем локальное значение при изменении props или при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        setLocalValue(
          tempValue === undefined || tempValue === null ? "" : String(tempValue)
        );
      } else {
        setLocalValue(
          value === undefined || value === null ? "" : String(value)
        );
      }
    }, [isEditing, value, tempValue]);

    // Автофокус и выделение текста при входе в режим редактирования
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    if (isEditing) {
      return (
        <input
          type={type}
          step={type === "number" ? "0.1" : undefined}
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            const raw = e.target.value;
            // Разрешаем только цифры и одну точку/запятую, убираем остальные символы
            const sanitized = raw
              .replace(/,/g, ".")
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            setLocalValue(sanitized);
            // Обновляем родительское состояние для синхронизации
            onTempValueChange(fieldKey, sanitized);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              onSaveEdit(sectionId, blockIndex, fieldName, localValue);
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setLocalValue(
                value === undefined || value === null ? "" : String(value)
              );
              onCancelEdit(sectionId, blockIndex, fieldName);
            }
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onBlur={() => {
            // Автоматически сохраняем при потере фокуса
            onSaveEdit(sectionId, blockIndex, fieldName, localValue);
          }}
          disabled={!isEditable || isCalculating || isLoading}
          style={{
            width: "60px",
            padding: "4px 8px",
            border: "1px solid #007bff",
            borderRadius: "4px",
            fontSize: "14px",
            textAlign: "center",
            outline: "none",
            background: "white",
          }}
        />
      );
    }

    return (
      <span
        style={{
          fontSize: "14px",
          display: "inline-block",
          cursor: isEditable ? "pointer" : "default",
          padding: "6px 10px",
          border: "1.5px dashed #B8C2D1",
          borderRadius: "6px",
          background: isEditable ? "#F9FBFF" : "#F4F6FA",
          color: "#1F2937",
          minWidth: "60px",
          textAlign: "center",
        }}
        onClick={() =>
          isEditable && onStartEdit(sectionId, blockIndex, fieldName, value)
        }
      >
        {format(value)}
      </span>
    );
  };

  return (
    <div className="work-tab">
      {/* Поле "Доля бонусов" для всех разделов */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            % доля бонусов от ПЗ:
          </label>
          {editingBonusPercent ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <input
                type="number"
                step="1"
                min="0"
                value={tempBonusPercent}
                ref={bonusInputRef}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Разрешаем только цифры, убираем остальные символы
                  const sanitized = raw.replace(/[^0-9]/g, "");
                  setTempBonusPercent(sanitized);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveBonusPercent(tempBonusPercent);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelBonusPercent();
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                style={{
                  width: "30px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  textAlign: "center",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ccc";
                  // Автоматически сохраняем при потере фокуса
                  handleSaveBonusPercent(tempBonusPercent);
                }}
                disabled={!isEditable || isCalculating || isLoading}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontFamily: "Manrope, sans-serif",
                  minWidth: "30px",
                  padding: "8px 12px",
                  border: "1.5px dashed #B8C2D1",
                  borderRadius: "6px",
                  background: isEditable ? "#F9FBFF" : "#F4F6FA",
                  textAlign: "center",
                  cursor: isEditable ? "pointer" : "default",
                }}
                onClick={() => isEditable && handleEditBonusPercent()}
              >
                {bonusPercent}
              </span>
            </div>
          )}
          <span style={{ fontSize: "12px", color: "#666" }}>
            ⚠️ Применяется ко всем разделам
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ fontSize: "14px", color: "#666" }}>
          💡 Изменения пересчитываются сразу при сохранении поля. Кнопка
          "Сохранить" внизу записывает все изменения в базу данных.
          {isCalculating && (
            <span style={{ marginLeft: "10px", color: "#007bff" }}>
              ⏳ Выполняется калькуляция...
            </span>
          )}
        </div>
      </div>

      {/* Таблица разделов */}
      <div className="work-tab-table">
        <table className="estimate-table">
          <thead>
            <tr>
              <th>Раздел</th>
              <th>Утвержденная сметная стоимость</th>
              <th>Плановая стоимость раздела</th>
              <th>% доля плановой стоимости</th>
              <th>Отклонение</th>
              <th>Стоимость за вычетом налогов с ФОТ</th>
              <th>Налоги с ФОТ</th>
              <th>Прямые затраты Бонусный фонд</th>
              <th>Прямые затраты Бонусный фонд</th>
            </tr>
          </thead>
          <tbody>
            {workData.sections.map((section, idx) => (
              <React.Fragment key={idx}>
                <tr className="estimate-table-row estimate-table-row-interactive">
                  <td className="section-cell">
                    <span
                      className="section-arrow"
                      onClick={() => handleExpandRow(idx)}
                      style={{
                        cursor: "pointer",
                        transform: expandedRows.has(idx)
                          ? "rotate(90deg)"
                          : "none",
                      }}
                    >
                      ▶
                    </span>
                    <span className="estimate_section-title">
                      {section.name}
                    </span>
                  </td>
                  <td className="currency-cell">
                    ₸ {formatCurrency(section.direct_costs)}
                  </td>
                  <td className="currency-cell">
                    ₸ {formatCurrency(section.total_costs)}
                  </td>
                  <td className="percent-cell">
                    × {section.expenditures_percent?.planned || 0}
                  </td>
                  <td
                    className={
                      section.different_in_expenditures >= 0 ? "green" : "red"
                    }
                  >
                    ₸ {formatCurrency(section.different_in_expenditures || 0)}
                  </td>
                  <td className="currency-cell">
                    ₸{" "}
                    {formatCurrency(
                      section.total_costs - section.payroll_taxes
                    )}
                  </td>
                  <td className="currency-cell">
                    ₸ {formatCurrency(section.payroll_taxes)}
                  </td>
                  <td className="multi-cell">
                    <div className="multi-line">
                      <div>{section.labor_days} дн</div>
                      <div>₸ {formatCurrency(section.direct_costs)}</div>
                    </div>
                  </td>
                  <td className="multi-cell">
                    <div className="multi-line">
                      <div>₸ {formatCurrency(section.direct_costs)}</div>
                      <div>₸ {formatCurrency(section.bonus_costs)}</div>
                    </div>
                  </td>
                </tr>

                {expandedRows.has(idx) && (
                  <tr className="estimate-table-expanded-row">
                    <td colSpan="9">
                      <div className="expanded-section-blocks">
                        <div className="expanded-section-hint">
                          💡 Кликните по числу, чтобы отредактировать.
                          Изменения сохраняются при потере фокуса.
                        </div>
                        {(() => {
                          const details = localSectionDetails[section.id];
                          const blocks = details?.block_expenditures ?? [];
                          const hasBlocks = blocks.length > 0;
                          if (details === undefined) {
                            return (
                              <div className="expanded-section-loading">
                                Загрузка…
                              </div>
                            );
                          }

                          const getSpecialistEffort = (sectionName, blockName, specIdx, block, totalSpecs) => {
                            const stored = getSpecialistEfforts(sectionName, blockName, specIdx);
                            const n = totalSpecs || 1;
                            const defaults = {
                              day_efforts: (block?.day_efforts ?? 0) / n,
                              bonus_day_efforts: (block?.bonus_day_efforts ?? 0) / n,
                              money_expenditures: Math.round((block?.money_expenditures ?? 0) / n),
                              bonus_money_expenditures: Math.round((block?.bonus_money_expenditures ?? 0) / n),
                            };
                            if (stored && typeof stored.day_efforts === "number") {
                              return { ...defaults, ...stored };
                            }
                            return defaults;
                          };
                          const getBlockTotalsFromSpecialists = (sectionName, blockName, block, specialists) => {
                            if (!specialists || specialists.length === 0) {
                              return {
                                day_efforts: block?.day_efforts ?? 0,
                                bonus_day_efforts: block?.bonus_day_efforts ?? 0,
                                money_expenditures: block?.money_expenditures ?? 0,
                                bonus_money_expenditures: block?.bonus_money_expenditures ?? 0,
                              };
                            }
                            return specialists.reduce(
                              (acc, _, specIdx) => {
                                const e = getSpecialistEffort(sectionName, blockName, specIdx, block, specialists.length);
                                acc.day_efforts += e.day_efforts ?? 0;
                                acc.bonus_day_efforts += e.bonus_day_efforts ?? 0;
                                acc.money_expenditures += (e.money_expenditures ?? 0);
                                acc.bonus_money_expenditures += (e.bonus_money_expenditures ?? 0);
                                return acc;
                              },
                              { day_efforts: 0, bonus_day_efforts: 0, money_expenditures: 0, bonus_money_expenditures: 0 }
                            );
                          };
                          const handleSaveSpecialistEffort = async (sectionName, blockName, specIdx, fieldName, value) => {
                            updateSpecialistEfforts(sectionName, blockName, specIdx, { [fieldName]: parseFloat(value) || 0 });
                            try {
                              setIsCalculating(true);
                              await performCalculation(projectId);
                            } finally {
                              setIsCalculating(false);
                            }
                          };

                          return (
                            <>
                              <table className="expanded-blocks-table expanded-blocks-list">
                                <thead>
                                  <tr>
                                    <th className="expanded-blocks-th-name"></th>
                                    <th>Трудозатраты</th>
                                    <th>Бонусы в днях (дн)</th>
                                    <th>Прямые затраты (тг)</th>
                                    <th>Бонусы в деньгах (тг)</th>
                                    <th className="expanded-blocks-th-action"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {hasBlocks
                                    ? blocks.map((block, blockIndex) => {
                                        const blockName =
                                          block.block?.name ||
                                          `Блок ${blockIndex + 1} 9 эт.`;
                                        const blockKey = `${idx}-${blockIndex}`;
                                        const isBlockExpanded =
                                          expandedBlocks.has(blockKey);
                                        const specialistKey = getSpecialistKey(
                                          section.name,
                                          blockName
                                        );
                                        const specialists =
                                          selectedSpecialists?.[specialistKey] ||
                                          [];

                                        return (
                                          <React.Fragment key={blockIndex}>
                                            <tr
                                              className="expanded-block-row"
                                              onClick={() =>
                                                toggleBlockExpanded(
                                                  idx,
                                                  blockIndex
                                                )
                                            }
                                            style={{
                                                cursor: "pointer",
                                              }}
                                            >
                                              <td className="expanded-blocks-td-name">
                                                <div className="expanded-block-name-cell">
                                                  <span
                                                    className="section-arrow expanded-block-arrow"
                                                    style={{
                                                      transform: isBlockExpanded
                                                        ? "rotate(90deg)"
                                                        : "none",
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleBlockExpanded(
                                                        idx,
                                                        blockIndex
                                                      );
                                                    }}
                                                  >
                                                    ▶
                                                  </span>
                                                  <span className="expanded-block-dot expanded-block-dot-default" />
                                                  <span>
                                                    {blockName}
                                                    {isBlockExpanded && specialists.length > 0 && (
                                                      <span className="expanded-block-specialists-count">
                                                        {" "}({specialists.length}{" "}
                                                        {specialists.length === 1
                                                          ? "исполнитель"
                                                          : specialists.length < 5
                                                          ? "исполнителя"
                                                          : "исполнителей"})
                                                      </span>
                                                    )}
                                                  </span>
                                                </div>
                                              </td>
                                              <td onClick={(e) => e.stopPropagation()}>
                                                {(() => {
                                                  const totals = getBlockTotalsFromSpecialists(
                                                    section.name,
                                                    blockName,
                                                    block,
                                                    specialists
                                                  );
                                                  return (totals.day_efforts ?? 0).toFixed(1);
                                                })()}
                                              </td>
                                              <td onClick={(e) => e.stopPropagation()}>
                                                {(() => {
                                                  const totals = getBlockTotalsFromSpecialists(
                                                    section.name,
                                                    blockName,
                                                    block,
                                                    specialists
                                                  );
                                                  return (totals.bonus_day_efforts ?? 0).toFixed(1);
                                                })()}
                                              </td>
                                              <td onClick={(e) => e.stopPropagation()}>
                                                <span className="tenge-inline">₸</span>{" "}
                                                {(() => {
                                                  const totals = getBlockTotalsFromSpecialists(
                                                    section.name,
                                                    blockName,
                                                    block,
                                                    specialists
                                                  );
                                                  return (totals.money_expenditures ?? 0).toLocaleString();
                                                })()}
                                              </td>
                                              <td onClick={(e) => e.stopPropagation()}>
                                                <span className="tenge-inline">₸</span>{" "}
                                                {(() => {
                                                  const totals = getBlockTotalsFromSpecialists(
                                                    section.name,
                                                    blockName,
                                                    block,
                                                    specialists
                                                  );
                                                  return (totals.bonus_money_expenditures ?? 0).toLocaleString();
                                                })()}
                                              </td>
                                              <td className="expanded-block-action-cell"></td>
                                            </tr>
                                            {isBlockExpanded && (
                                              <>
                                                {specialists.map((specialist, specIdx) => {
                                                  const effort = getSpecialistEffort(
                                                    section.name,
                                                    blockName,
                                                    specIdx,
                                                    block,
                                                    specialists.length
                                                  );
                                                  const specLabel =
                                                    specialist.type === "employee"
                                                      ? specialist.fullName || specialist.name || `Специалист ${specIdx + 1}`
                                                      : specialist.fullName || specialist.name || `Субподрядчик ${specIdx + 1}`;
                                                  return (
                                                    <tr
                                                      key={specIdx}
                                                      className="expanded-specialist-row"
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      <td className="expanded-blocks-td-name">
                                                        <div className="expanded-specialist-name-cell">
                                                          <span className="expanded-specialist-dot" />
                                                          {specLabel}
                                                        </div>
                                                      </td>
                                                      <td>
                                                        <EditableField
                                                          sectionId={section.id}
                                                          blockIndex={`spec-${blockIndex}-${specIdx}`}
                                                          fieldName="day_efforts"
                                                          value={effort.day_efforts ?? 0}
                                                          isEditing={
                                                            editingFields[
                                                              `${section.id}-spec-${blockIndex}-${specIdx}-day_efforts`
                                                            ]
                                                          }
                                                          tempValue={
                                                            tempValues[
                                                              `${section.id}-spec-${blockIndex}-${specIdx}-day_efforts`
                                                            ]
                                                          }
                                                          onStartEdit={handleStartEdit}
                                                          onSaveEdit={(_sid, _bid, _field, val) => {
                                                            const fieldKey = `${section.id}-spec-${blockIndex}-${specIdx}-day_efforts`;
                                                            setEditingFields((prev) => ({ ...prev, [fieldKey]: false }));
                                                            handleSaveSpecialistEffort(section.name, blockName, specIdx, "day_efforts", val);
                                                          }}
                                                          onCancelEdit={handleCancelEdit}
                                                          onTempValueChange={handleTempValueChange}
                                                          format={(v) => (v ?? 0).toFixed(1)}
                                                          isEditable={isEditable}
                                                        />
                                                      </td>
                                                      <td>{(effort.bonus_day_efforts ?? 0).toFixed(1)}</td>
                                                      <td>
                                                        <span className="tenge-inline">₸</span>{" "}
                                                        {(effort.money_expenditures ?? 0).toLocaleString()}
                                                      </td>
                                                      <td>
                                                        <span className="tenge-inline">₸</span>{" "}
                                                        {(effort.bonus_money_expenditures ?? 0).toLocaleString()}
                                                      </td>
                                                      <td className="expanded-block-action-cell">
                                                        {isEditable && (
                                                          <button
                                                            type="button"
                                                            className="expanded-specialist-delete-btn"
                                                            onClick={() =>
                                                              handleSpecialistDelete(
                                                                blockName,
                                                                section.name,
                                                                specIdx
                                                              )
                                                            }
                                                            title="Удалить"
                                                          >
                                                            🗑
                                                          </button>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                                <tr className="expanded-block-add-row">
                                                  <td colSpan="6" className="expanded-block-performers-cell">
                                                    <div
                                                      className="expanded-block-performers"
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      <button
                                                        type="button"
                                                        className="add-performer-btn"
                                                        onClick={() =>
                                                          isEditable &&
                                                          onAddSpecialist &&
                                                          onAddSpecialist(
                                                            blockName,
                                                            section.name
                                                          )
                                                        }
                                                        disabled={!isEditable}
                                                      >
                                                        <span className="add-performer-btn-icon">+</span>
                                                        Добавить исполнителя
                                                      </button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              </>
                                            )}
                                          </React.Fragment>
                                        );
                                      })
                                    : null}
                                </tbody>
                              </table>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {/* Строка итогов */}
            <tr className="estimate-table-total">
              <td>
                <strong>ВСЕ РАЗДЕЛЫ</strong>
              </td>
              <td>
                <strong>₸ {formatCurrency(28245830)}</strong>
              </td>
              <td>
                <strong>₸ {formatCurrency(22879263)}</strong>
              </td>
              <td>
                <strong>% 100</strong>
              </td>
              <td className="green">
                <strong>₸ {formatCurrency(3361290)}</strong>
              </td>
              <td>
                <strong>₸ {formatCurrency(24884539)}</strong>
              </td>
              <td>
                <strong>₸ {formatCurrency(2005276)}</strong>
              </td>
              <td>
                <div className="multi-line">
                  <div>
                    <strong>1 195 дн</strong>
                  </div>
                  <div>
                    <strong>₸ {formatCurrency(workData.direct_costs)}</strong>
                  </div>
                </div>
              </td>
              <td>
                <div className="multi-line">
                  <div>
                    <strong>₸ {formatCurrency(workData.direct_costs)}</strong>
                  </div>
                  <div>
                    <strong>₸ {formatCurrency(workData.bonus_fund)}</strong>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkTab;
